/**
 * React hooks for the Firebase side of televisit.
 *
 * - useFirebaseAuthSync(): wires the AuthProvider's Keycloak token into
 *   the Firebase Auth bridge; resolves to { ready, error }.
 * - useTenantQueue(tenantId): subscribes to the realtime queue under
 *   televisit-queue/{tenantId}/patients. Returns the live list +
 *   loading/error state.
 *
 * Both hooks gate on Firebase Auth being signed in so Firestore rules
 * see a `request.auth.token.tenantId` matching the path wildcard.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { firestore } from './config';
import { onFirebaseAuthChanged, signInWithKeycloak } from './auth-bridge';
import { useAuth } from '../auth/AuthProvider';
import { useSessionStore } from '../stores/sessionStore';

/**
 * Bridge the Keycloak session into Firebase Auth. Idempotent — safe
 * to mount in multiple components; the underlying signInWithKeycloak
 * dedupes concurrent calls.
 */
export function useFirebaseAuthSync() {
  const { token, authMode } = useAuth();
  // STAB-02 — pass the launch partition (from sandman launch JWT's
  // `tenant` claim, stashed in sessionStore.tenantId at launch time)
  // into the Firebase custom token exchange. Firestore rules then see
  // `request.auth.token.tenantId == <launch partition>` and the
  // queue/transcript/ai-agent reads succeed in the launch tenant.
  const launchPartition = useSessionStore((s) => s.tenantId);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  // 1. Track Firebase auth state so we know when sign-in lands.
  useEffect(() => {
    const unsub = onFirebaseAuthChanged((u) => setReady(!!u));
    return unsub;
  }, []);

  // 2. When the KC token shows up (post-launch), exchange it for a
  //    Firebase custom token + sign in. Re-runs if the launch
  //    partition changes (e.g., user re-launches into a different
  //    tenant) — the bridge detects the mismatch and re-exchanges.
  useEffect(() => {
    if (!token) return;
    if (authMode === 'dev') return; // dev-bypass — no real auth
    let cancelled = false;
    signInWithKeycloak(token, { launchPartition })
      .catch((err) => {
        if (cancelled) return;
        console.error('[Firebase] auth bridge failed:', err);
        setError(err);
      });
    return () => { cancelled = true; };
  }, [token, authMode, launchPartition]);

  return { ready, error };
}

/**
 * Subscribe to the tenant's realtime visit queue.
 *
 * @param {string|null} tenantId - the active tenant (from sessionStore).
 *   When null, the hook idles and returns empty state.
 * @param {object} options
 * @param {string[]} [options.statuses=['waiting','in-visit']] - which
 *   queue buckets to include.
 * @returns {{ rows: object[], isLoading: boolean, error: Error|null }}
 */
export function useTenantQueue(tenantId, options = {}) {
  const statuses = options.statuses || ['waiting', 'in-visit'];
  const { ready: fbReady, error: fbErr } = useFirebaseAuthSync();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoize the Firestore query so we don't churn listeners.
  const q = useMemo(() => {
    if (!tenantId || !firestore) return null;
    return query(
      collection(firestore, 'televisit-queue', tenantId, 'patients'),
      where('status', 'in', statuses),
      orderBy('joinedAt', 'asc'),
    );
    // `statuses` is a stable array; safe to depend on its JSON form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, JSON.stringify(statuses)]);

  useEffect(() => {
    if (!fbReady || !q) {
      setRows([]);
      setIsLoading(!!tenantId && !fbReady);
      return;
    }
    setIsLoading(true);
    setError(null);
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setIsLoading(false);
      },
      (err) => {
        console.error('[Firebase] useTenantQueue snapshot error:', err);
        setError(err);
        setIsLoading(false);
      },
    );
    return unsub;
  }, [q, fbReady, tenantId]);

  return {
    rows,
    isLoading,
    error: error || fbErr,
  };
}

// ── Patient-side hooks (TV-P2-03 + TV-P2-04) ────────────────────────────────

const DEFAULT_INTAKE = Object.freeze({
  consent: false,
  questionnaire: false,
  av: false,
});

/**
 * TV-P2-04 — patient writes their own queue doc on mount, leaves on
 * unmount.
 *
 * The doc key is the patient's **Firebase Auth UID** (== KC sub),
 * NOT the FHIR Patient id. The Firestore rule predicate is
 * `request.auth.uid == patientId` (the path wildcard), so anything
 * keyed by a non-auth.uid path would be rejected. The patient's
 * FHIR Patient id (e.g. `-48`) goes into the body's `patientId`
 * field — the provider's ProviderQueue splice still matches on that
 * body field against FHIR Appointment.patient references.
 *
 * `presence` is the small body payload:
 *   { patientId, patientName, appointmentId, encounterId,
 *     chiefComplaint, triageLevel }
 * All fields optional. `patientId` is the FHIR id when known —
 * lets the provider's UI deduplicate against the appointment list.
 *
 * Idempotent re-writes are safe (uses setDoc + merge). Cleanup on
 * unmount flips status to 'left' so the provider sees the patient
 * drop off without leaving an orphan row.
 */
export function useJoinQueue(uid, tenantId, presence = {}) {
  const { ready: fbReady } = useFirebaseAuthSync();
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState(null);
  // Capture the doc ref + cleanup function in refs so React StrictMode's
  // double-effect doesn't lose the path between mount and unmount.
  const docRefRef = useRef(null);
  const writtenRef = useRef(false);

  useEffect(() => {
    if (!fbReady || !uid || !tenantId || !firestore) {
      // Loud-on-the-floor diagnostic: silent bails were eating writes
      // and producing the '0/3 with no banner' symptom. If any of the
      // prerequisites is missing, say so.
      if (fbReady && !uid) {
        console.warn(
          '[Firebase] useJoinQueue: fbReady but no uid — auth user has no id. ' +
            'This means the KC token may be missing the sub claim. ' +
            'Tenant:', tenantId, 'Firebase auth currentUser?:',
          firestore ? 'firestore ok' : 'firestore null',
        );
      }
      return undefined;
    }

    const ref = doc(firestore, 'televisit-queue', tenantId, 'patients', uid);
    docRefRef.current = ref;
    let cancelled = false;

    (async () => {
      try {
        await setDoc(
          ref,
          {
            // Body fields — FHIR patientId for provider matching;
            // doc id (uid) handles the Firestore rule.
            patientId: presence.patientId || null,
            patientUid: uid,
            patientName: presence.patientName || null,
            appointmentId: presence.appointmentId || null,
            encounterId: presence.encounterId || null,
            chiefComplaint: presence.chiefComplaint || null,
            triageLevel: presence.triageLevel || 'blue',
            intakeProgress: { ...DEFAULT_INTAKE },
            status: 'waiting',
            joinedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        if (!cancelled) {
          writtenRef.current = true;
          setJoined(true);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[Firebase] useJoinQueue write failed:', err);
        setError(err);
      }
    })();

    return () => {
      cancelled = true;
      const r = docRefRef.current;
      if (!r || !writtenRef.current) return;
      // Best-effort 'left' marker. Don't block unmount on it.
      updateDoc(r, {
        status: 'left',
        leftAt: serverTimestamp(),
      }).catch((err) => {
        console.warn('[Firebase] useJoinQueue cleanup write failed:', err);
      });
    };
    // presence is captured at first mount — subsequent presence prop
    // changes shouldn't re-create the doc; use the updater hook below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fbReady, uid, tenantId]);

  return { joined, error };
}

/**
 * TV-P2-04b — patch the patient's queue doc with a partial update.
 * Used by TV-P2-05 (intake checklist) + TV-P2-06 (A/V pre-test) to
 * flip individual intakeProgress fields without re-writing the whole
 * doc. Returns a stable `patch` function. `uid` is the Firebase Auth
 * UID (doc key) — matches what useJoinQueue writes.
 */
export function useUpdateQueueDoc(uid, tenantId) {
  return useMemo(() => {
    return async (partial) => {
      if (!uid || !tenantId || !firestore) return;
      const ref = doc(firestore, 'televisit-queue', tenantId, 'patients', uid);
      await updateDoc(ref, { ...partial, updatedAt: serverTimestamp() });
    };
  }, [uid, tenantId]);
}

/**
 * TV-P2-03 — listen to the patient's own queue doc + compute position
 * in line. `position` is 1-indexed; "1" means you're next. `null`
 * before the doc + the queue snapshot land.
 *
 * `uid` is the Firebase Auth UID (== doc key). Self-lookup in the
 * waiting list is `r.id === uid`, NOT a body-field match — the body's
 * `patientId` is the FHIR id, which doesn't always exist for every
 * patient. The doc id is always present and is auth-attested.
 *
 * Estimated wait = (position - 1) * avgVisitMinutes (default 15).
 *
 * Returns { myDoc, position, estimatedMinutes, total, isLoading, error }.
 */
export function useMyQueuePosition(uid, tenantId, options = {}) {
  const avgVisitMinutes = options.avgVisitMinutes ?? 15;
  const { ready: fbReady } = useFirebaseAuthSync();
  const [myDoc, setMyDoc] = useState(null);
  const [waitingList, setWaitingList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Subscribe to the patient's own doc.
  useEffect(() => {
    if (!fbReady || !uid || !tenantId || !firestore) {
      setMyDoc(null);
      setIsLoading(!!uid && !fbReady);
      return undefined;
    }
    const ref = doc(firestore, 'televisit-queue', tenantId, 'patients', uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setMyDoc(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setIsLoading(false);
      },
      (err) => {
        console.error('[Firebase] useMyQueuePosition own-doc error:', err);
        setError(err);
        setIsLoading(false);
      },
    );
    return unsub;
  }, [fbReady, uid, tenantId]);

  // Subscribe to the tenant's waiting queue (sorted by joinedAt) so we
  // can compute position. Using the same composite index as the
  // provider listener (status==waiting, joinedAt ASC).
  useEffect(() => {
    if (!fbReady || !tenantId || !firestore) return undefined;
    const q = query(
      collection(firestore, 'televisit-queue', tenantId, 'patients'),
      where('status', '==', 'waiting'),
      orderBy('joinedAt', 'asc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setWaitingList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error('[Firebase] useMyQueuePosition list error:', err);
        setError(err);
      },
    );
    return unsub;
  }, [fbReady, tenantId]);

  const position = useMemo(() => {
    if (!uid || waitingList.length === 0) return null;
    const idx = waitingList.findIndex((r) => r.id === uid);
    return idx === -1 ? null : idx + 1;
  }, [waitingList, uid]);

  const estimatedMinutes = position != null ? Math.max(0, (position - 1) * avgVisitMinutes) : null;

  return {
    myDoc,
    position,
    estimatedMinutes,
    total: waitingList.length,
    isLoading,
    error,
  };
}
